import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { BRAND } from '@fin/shared/brand';
import { EMIRATES } from '@fin/shared/enums';

import { Button, Card, Field, PageHeader, inputClass } from '../components/ui';
import { useCustomer, usePatchCustomer, useDeleteCustomer } from '../lib/queries';

const TZ = 'Asia/Dubai';
function fmt(s?: string) {
  if (!s) return '—';
  try {
    return new Intl.DateTimeFormat('en-AE', {
      timeZone: TZ,
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(s));
  } catch {
    return s;
  }
}

function formatAddress(c: { address: { building?: string; street?: string; area: string; emirate: string; makaniNumber?: string } }): string {
  const parts = [
    c.address.building,
    c.address.street,
    c.address.area,
    c.address.emirate.replace('_', ' '),
    c.address.makaniNumber ? `Makani ${c.address.makaniNumber}` : undefined,
    'United Arab Emirates',
  ].filter(Boolean);
  return parts.join(', ');
}

export default function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const cust = useCustomer(id);
  const patch = usePatchCustomer();
  const del = useDeleteCustomer();
  const [editing, setEditing] = useState(false);

  async function handleDelete(name: string, cid: string) {
    if (!window.confirm(`Delete customer "${name}"? This can't be undone.`)) return;
    try {
      await del.mutateAsync(cid);
      navigate('/customers', { replace: true });
    } catch (err) {
      alert(err instanceof Error ? err.message : String(err));
    }
  }

  if (cust.isPending) return <p className="text-stone-500">Loading…</p>;
  if (cust.isError)
    return <p style={{ color: BRAND.danger }}>Failed to load: {(cust.error as Error).message}</p>;
  if (!cust.data) return null;
  const c = cust.data;

  return (
    <>
      <PageHeader
        title={c.name}
        action={
          <div className="flex gap-2">
            <Link to="/customers" className="text-sm text-stone-500 hover:underline">
              ← All customers
            </Link>
            <Button onClick={() => setEditing((v) => !v)}>{editing ? 'Cancel' : 'Edit'}</Button>
            <button
              type="button"
              onClick={() => handleDelete(c.name, c.id)}
              disabled={del.isPending}
              className="rounded-md border px-3 py-1.5 text-sm font-medium transition hover:bg-red-50 disabled:opacity-50"
              style={{ borderColor: BRAND.danger, color: BRAND.danger }}
            >
              {del.isPending ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        }
      />

      {editing ? (
        <EditCustomerForm
          initial={c}
          submitting={patch.isPending}
          onCancel={() => setEditing(false)}
          onSave={async (p) => {
            await patch.mutateAsync({ id: c.id, patch: p });
            setEditing(false);
          }}
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <div className="border-b border-stone-200 px-4 py-3 text-xs uppercase tracking-wide text-stone-500">
              Contact
            </div>
            <div className="divide-y divide-stone-100">
              <Row label="Phone">{c.phone}</Row>
              <Row label="WhatsApp">{c.whatsappPhone ?? c.phone}</Row>
              <Row label="Email">{c.email ?? '—'}</Row>
              <Row label="Created">{fmt(c.createdAt)}</Row>
              <Row label="Updated">{fmt(c.updatedAt)}</Row>
            </div>
          </Card>
          <Card>
            <div className="border-b border-stone-200 px-4 py-3 text-xs uppercase tracking-wide text-stone-500">
              Address
            </div>
            <div className="divide-y divide-stone-100">
              <Row label="Building">{c.address.building ?? '—'}</Row>
              <Row label="Unit">{c.address.unit ?? '—'}</Row>
              <Row label="Street">{c.address.street ?? '—'}</Row>
              <Row label="Area">{c.address.area}</Row>
              <Row label="Emirate">{c.address.emirate.replace('_', ' ')}</Row>
              <Row label="Makani">{c.address.makaniNumber ?? '—'}</Row>
            </div>
          </Card>
          <Card className="lg:col-span-2">
            <div className="flex items-center justify-between border-b border-stone-200 px-4 py-3">
              <span className="text-xs uppercase tracking-wide text-stone-500">Map</span>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formatAddress(c))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs hover:underline"
                style={{ color: BRAND.primary }}
              >
                Open in Google Maps ↗
              </a>
            </div>
            {/* Google's `maps?q=…&output=embed` iframe is now frame-blocked
                (net::ERR_BLOCKED_BY_RESPONSE). Free-text addresses can't be
                embedded keylessly, so show a tap-to-open Maps card instead. */}
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formatAddress(c))}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-72 w-full items-center justify-center gap-2 rounded-b text-sm font-medium hover:underline"
              style={{ backgroundColor: BRAND.surfaceMuted, color: BRAND.primary }}
            >
              <span aria-hidden className="text-2xl">🗺️</span> Open in Google Maps ↗
            </a>
          </Card>
          {c.notes && (
            <Card>
              <div className="border-b border-stone-200 px-4 py-3 text-xs uppercase tracking-wide text-stone-500">
                Notes
              </div>
              <div className="whitespace-pre-wrap px-4 py-3 text-sm text-stone-700">{c.notes}</div>
            </Card>
          )}
        </div>
      )}
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-3 px-4 py-3 text-sm">
      <div className="text-stone-500">{label}</div>
      <div className="col-span-2 text-stone-800">{children}</div>
    </div>
  );
}

interface EditableCustomer {
  id: string;
  name: string;
  phone: string;
  whatsappPhone?: string;
  email?: string;
  notes?: string;
  address: {
    building?: string;
    unit?: string;
    street?: string;
    area: string;
    emirate: string;
    makaniNumber?: string;
  };
}

function EditCustomerForm({
  initial,
  submitting,
  onCancel,
  onSave,
}: {
  initial: EditableCustomer;
  submitting: boolean;
  onCancel: () => void;
  onSave: (patch: Partial<EditableCustomer>) => Promise<void>;
}) {
  const [name, setName] = useState(initial.name);
  const [phone, setPhone] = useState(initial.phone);
  const [whatsapp, setWhatsapp] = useState(initial.whatsappPhone ?? '');
  const [email, setEmail] = useState(initial.email ?? '');
  const [notes, setNotes] = useState(initial.notes ?? '');
  const [building, setBuilding] = useState(initial.address.building ?? '');
  const [area, setArea] = useState(initial.address.area);
  const [emirate, setEmirate] = useState<(typeof EMIRATES)[number]>(
    initial.address.emirate as (typeof EMIRATES)[number],
  );
  const [makani, setMakani] = useState(initial.address.makaniNumber ?? '');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await onSave({
        name,
        phone,
        whatsappPhone: whatsapp || undefined,
        email: email || undefined,
        notes: notes || undefined,
        address: {
          building: building || undefined,
          area,
          emirate,
          makaniNumber: makani || undefined,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <Card>
      <form onSubmit={onSubmit} className="flex flex-col gap-4 p-4">
        {error && (
          <div
            role="alert"
            className="rounded border px-3 py-2 text-xs"
            style={{ borderColor: BRAND.danger, color: BRAND.danger, backgroundColor: '#FEF2F2' }}
          >
            {error}
          </div>
        )}
        <Field label="Name">
          <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Phone (+971…)">
            <input className={inputClass} value={phone} onChange={(e) => setPhone(e.target.value)} required />
          </Field>
          <Field label="WhatsApp (optional)">
            <input className={inputClass} value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} />
          </Field>
        </div>
        <Field label="Email (optional)">
          <input className={inputClass} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Building">
            <input className={inputClass} value={building} onChange={(e) => setBuilding(e.target.value)} />
          </Field>
          <Field label="Area">
            <input className={inputClass} value={area} onChange={(e) => setArea(e.target.value)} required />
          </Field>
          <Field label="Emirate">
            <select
              className={inputClass}
              value={emirate}
              onChange={(e) => setEmirate(e.target.value as (typeof EMIRATES)[number])}
            >
              {EMIRATES.map((em) => (
                <option key={em} value={em}>
                  {em.replace('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Makani #">
            <input className={inputClass} value={makani} onChange={(e) => setMakani(e.target.value)} />
          </Field>
        </div>
        <Field label="Notes (internal)">
          <textarea
            className={inputClass}
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="text-sm text-stone-500 hover:underline">
            Cancel
          </button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
